import { useEffect, useState, useCallback } from 'react';
import { Stack, Paper, LoadingOverlay, Button, Text, Alert, Modal } from '@mantine/core';
import { usePlaidLink, PlaidLinkOnSuccess, PlaidLinkOnExit, PlaidLinkError } from 'react-plaid-link';
import { User } from '@propelauth/react';
import { env } from '../utils/env';
import AccountList from './AccountList';
import LinkUpdateHandler from './LinkUpdateHandler';
import axios from 'axios';
import { showSuccessNotification, showErrorNotification } from '../utils/notificationUtils';
import { handlePlaidNotification } from '../utils/notificationUtils';
import PlaidPreLinkMessaging from './PlaidPreLinkMessaging';
import { useUnreadNotifications } from '../hooks/useUnreadNotifications';
import { useAccounts } from '../hooks/useAccounts';

interface AccountsTabProps {
  accessToken: string;
  user: User;
  isVisible?: boolean;
}

interface PlaidLinkMetadata {
  institution?: {
    name: string;
    institution_id: string;
  };
  accounts: Array<{
    id: string;
    name: string;
    mask: string;
    type: string;
    subtype: string;
  }>;
  link_session_id: string;
}

export const AccountsTab = ({ accessToken, user, isVisible = true }: AccountsTabProps) => {
  // Use the accounts hook with visibility prop
  const { accounts, isLoading, error: accountsError, refetch: fetchAccounts } = useAccounts(accessToken, { isVisible });
  const [error, setError] = useState<string | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);
  const [showLinkHandler, setShowLinkHandler] = useState(false);
  const [hasAccountsNeedingReconnection, setHasAccountsNeedingReconnection] = useState(false);
  const [showPreLinkMessaging, setShowPreLinkMessaging] = useState(false);

  // Use the SSE hook for real-time notifications
  const { notifications, status: sseStatus } = useUnreadNotifications(accessToken);

  // Set error from accounts hook
  useEffect(() => {
    if (accountsError) {
      setError(accountsError instanceof Error ? accountsError.message : 'Failed to load accounts. Please try again.');
    } else {
      setError(null);
    }
  }, [accountsError]);

  // Detect accounts needing reconnection
  useEffect(() => {
    const needsReconnection = accounts?.some(account =>
      ['error', 'needs_reauth', 'pending_disconnect', 'pending_expiration', 'revoked'].includes(account.status)
    );
    setHasAccountsNeedingReconnection(needsReconnection || false);
  }, [accounts]);

  // Fetch accounts when userId changes or when tab becomes visible
  useEffect(() => {
    if (user?.userId && isVisible) {
      fetchAccounts();
    }
  }, [user?.userId, fetchAccounts, isVisible]);

  // Process notifications from SSE in real-time
  useEffect(() => {
    if (!notifications?.length) return;

    // Process relevant notifications (Plaid-related)
    const plaidNotifications = notifications.filter(
      n => ['LOGIN_REPAIRED', 'NEW_ACCOUNTS_AVAILABLE', 'ITEM_UPDATE'].includes(n.type)
    );

    if (plaidNotifications.length > 0) {
      plaidNotifications.forEach(notification => {
        handlePlaidNotification(notification, fetchAccounts);
      });
    }
  }, [notifications, fetchAccounts]);

  // Update createLinkToken to show pre-link messaging first
  const createLinkToken = async () => {
    if (!accessToken) return;

    // Show pre-link messaging instead of immediately creating token
    setShowPreLinkMessaging(true);
  };

  // Add function to handle continuing from pre-link messaging
  const handlePreLinkContinue = async () => {
    if (!accessToken) return;

    try {
      setIsLinking(true);
      setError(null);
      setShowPreLinkMessaging(false);

      // Get the current URL for use as redirect_uri for OAuth flow
      const redirect_uri = `${window.location.origin}/plaid-oauth-redirect`;

      const response = await fetch(`${env.API_URL}/api/financial/link/token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          redirect_uri,
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Raw error response from AccountsTab:', errorText);

        let errorData: {
          error_code?: string;
          error_message?: string;
          detail?: string;
        } = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          console.error('Failed to parse error response as JSON:', e);
        }

        // Check for duplicate institution error
        if (errorData.error_code === 'ITEM_ERROR' &&
          errorData.error_message &&
          errorData.error_message.includes('already linked')) {
          throw new Error(`You have already linked this institution to your account.`);
        }

        throw new Error(errorData.detail || 'Failed to create link token');
      }

      const data = await response.json();
      setLinkToken(data.link_token);

      // Store link token in localStorage for OAuth redirect flow
      localStorage.setItem('plaid_link_token', data.link_token);

      // Show Link component
      setShowLinkHandler(true);

    } catch (err) {
      console.error('Error creating link token:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize account linking. Please try again.');
      setIsLinking(false);
    }
  };

  // Handle canceling from pre-link messaging
  const handlePreLinkCancel = () => {
    setShowPreLinkMessaging(false);
  };

  // Handle successful linking
  const handleLinkSuccess = async () => {
    console.log('Link success callback called');

    // Check if exchange was already completed by LinkUpdateHandler
    const exchangeCompleted = localStorage.getItem('plaid_exchange_completed') === 'true';
    if (exchangeCompleted) {
      console.log('Token already exchanged by LinkUpdateHandler, skipping duplicate exchange');
      localStorage.removeItem('plaid_exchange_completed');
      fetchAccounts();
      setIsLinking(false);
      setShowLinkHandler(false);
      return;
    }

    // Fallback path - exchange token if LinkUpdateHandler didn't complete it
    const publicToken = localStorage.getItem('plaid_public_token');
    const metadataString = localStorage.getItem('plaid_metadata');

    if (!publicToken || !metadataString) {
      console.log('No publicToken or metadata found in localStorage');
      fetchAccounts();
      return;
    }

    try {
      const metadata = JSON.parse(metadataString);
      console.log('Exchanging public token (fallback path):', publicToken.substring(0, 5) + '...');

      const response = await fetch(`${env.API_URL}/api/financial/link/exchange`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          public_token: publicToken,
          metadata
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error exchanging token (fallback path):', errorData);
        throw new Error(errorData.detail || 'Failed to link account');
      }

      const data = await response.json();
      console.log('Token exchange successful (fallback path):', data);

      // Clean up localStorage
      localStorage.removeItem('plaid_public_token');
      localStorage.removeItem('plaid_metadata');
      localStorage.removeItem('plaid_exchange_completed');

      showSuccessNotification('Account linked successfully');
    } catch (error) {
      console.error('Error in fallback token exchange:', error);
      showErrorNotification(error, 'Failed to link account');
    } finally {
      // Refresh accounts regardless of success/failure
      fetchAccounts();
      setIsLinking(false);
      setShowLinkHandler(false);
    }
  };

  // Add callback to handle Link exit
  const handleLinkExit = (error: PlaidLinkError | null) => {
    console.log('Link exited in AccountsTab', error);
    setIsLinking(false); // Reset loading state
    setShowLinkHandler(false); // Hide the handler component
    localStorage.removeItem('plaid_link_token'); // Clean up stored token
    // No need to show error here, LinkUpdateHandler does it
  };

  return (
    <Stack className="w-full p-4" style={{ position: 'relative' }}>
      <LoadingOverlay visible={isLoading} />
      <Paper radius="md" withBorder p="lg">
        {error && (
          <Alert color="red" mb="md" onClose={() => setError(null)} withCloseButton>
            {error}
          </Alert>
        )}

        {hasAccountsNeedingReconnection && (
          <Alert color="orange" mb="md" title="Action Required" withCloseButton={false}>
            One or more of your accounts need to be reconnected. Please use the "Reconnect" button next to the affected account(s).
          </Alert>
        )}

        <Button
          onClick={createLinkToken}
          loading={isLinking}
          mb="lg"
          leftSection={<Text>+</Text>}
          disabled={!accessToken || isLinking}
        >
          Link Account
        </Button>

        <AccountList
          accounts={accounts}
          onRefresh={fetchAccounts}
          loading={isLoading}
          accessToken={accessToken}
          currentUser={user}
        />

        {/* Import and add the PlaidPreLinkMessaging component */}
        {showPreLinkMessaging && (
          <Modal
            opened={showPreLinkMessaging}
            onClose={handlePreLinkCancel}
            size="lg"
            centered
            title=""
          >
            <PlaidPreLinkMessaging
              accessToken={accessToken}
              onContinue={handlePreLinkContinue}
              onCancel={handlePreLinkCancel}
            />
          </Modal>
        )}

        {/* Add the LinkUpdateHandler component for new accounts */}
        {showLinkHandler && linkToken && (
          <LinkUpdateHandler
            linkToken={linkToken}
            accessToken={accessToken}
            itemId={undefined}
            onSuccess={handleLinkSuccess}
            onExitCallback={handleLinkExit}
            isOauth={window.location.href.includes('oauth_state_id=')}
          />
        )}
      </Paper>
    </Stack>
  );
};

export default AccountsTab; 